import { create } from "zustand";
import { doc, onSnapshot } from "firebase/firestore";
import { getSearchApiBaseUrl } from "../apiConfig";
import { getAuthHeader } from "./auth";
import { firebaseHelper } from "./firebase";

export interface CreditBalance {
  totalBalance: number;
  stripeBalance?: number;
  freeBalance?: number;
  cachedUnbilledUsage?: number;
  lastUpdated?: number | null;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message || fallback;
  if (typeof error === "string") return error;
  return fallback;
}

/** Convert a Firestore Timestamp (or already-numeric value) to epoch millis. */
function toMillis(raw: unknown): number | null {
  if (raw && typeof (raw as any).toMillis === "function") {
    return (raw as any).toMillis();
  }
  return (raw as number) ?? null;
}

interface CreditStoreState {
  balance: CreditBalance | null;
  teamBalance: CreditBalance | null;
  designFirmId: string | null;
  hideTeamBalance: boolean;
  pendingArtifactCredits: number;
  isLoading: boolean;
  error: string | null;

  addPendingArtifactCredits: (n: number) => void;
  fetchBalance: (force?: boolean, uid?: string) => Promise<void>;
  fetchTeamBalance: (designFirmId: string, force?: boolean) => Promise<void>;
  checkSufficientCredits: (required: number) => boolean;
  subscribeToBalance: (uid: string) => void;
  subscribeToTeamBalance: (designFirmId: string) => void;
  unsubscribeAll: () => void;
  resetStore: () => void;
}

// Module-level unsubscribe references (not stored in Zustand state)
let personalUnsubscribe: (() => void) | null = null;
let teamUnsubscribe: (() => void) | null = null;

// Optimistic update guard: skip Firestore snapshots until this epoch ms
let optimisticUntil = 0;

/** Doc ids whose invalidation sentinel we've already acted on.
 *  Prevents a re-delivered snapshot from firing a second Stripe wallet call. */
const sentinelHandledFor = new Set<string>();

/** How long to suppress the Firestore listener after an optimistic credit update (ms).
 *  Must exceed the typical Stripe webhook processing time. */
export const OPTIMISTIC_SUPPRESS_MS = 5000;

/** Suppress Firestore balance listener for `ms` milliseconds after an optimistic update. */
export function suppressBalanceListener(ms: number = OPTIMISTIC_SUPPRESS_MS) {
  optimisticUntil = Date.now() + ms;
}

// UID-scoped dedup state for signup bonus check
let currentUid: string | null = null;
let bonusCheckedForUid: string | null = null;
let bonusInFlightUid: string | null = null;
let bonusInFlight: Promise<boolean> | null = null;

// In-flight dedup for wallet fetch — prevents N tabs from firing N Stripe calls
let walletFetchInFlight: Promise<void> | null = null;
let teamWalletFetchInFlight: Map<string, Promise<void>> = new Map();

const DEFAULT_TIMEOUT_MS = 15000;

async function callCreditsApi<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const base = getSearchApiBaseUrl();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await getAuthHeader()),
    ...(options.headers as Record<string, string>),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status}`);
    }

    return (await res.json()) as T;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timed out - server may be unavailable");
    }
    throw error;
  }
}

/**
 * Deduped signup bonus check. Shares in-flight requests for the same uid,
 * flags on success only (transient failures allow retry), and guards against
 * stale resolutions after resetStore or user switch.
 *
 * Exported for surfaces that never load a wallet (the SketchUp panel calls it
 * once after creating a new profile); the website still reaches it through
 * fetchBalance/subscribeToBalance.
 */
export async function checkSignupBonus(uid: string): Promise<boolean> {
  if (!uid) return false;
  if (bonusCheckedForUid === uid) return false;
  if (bonusInFlight && bonusInFlightUid === uid) return bonusInFlight;

  bonusInFlightUid = uid;
  bonusInFlight = (async () => {
    try {
      const result = await callCreditsApi<{
        ok: boolean;
        granted: boolean;
      }>("/api/credits/allocate-signup-bonus", { method: "POST" });
      if (bonusInFlightUid === uid) {
        bonusCheckedForUid = uid;
      }
      return result.granted === true;
    } catch (err) {
      console.warn("[CreditStore] Signup bonus check failed:", err);
      return false;
    } finally {
      if (bonusInFlightUid === uid) {
        bonusInFlight = null;
        bonusInFlightUid = null;
      }
    }
  })();

  return bonusInFlight;
}

/**
 * Create a Firestore onSnapshot listener for a creditBalances doc.
 * Returns the unsubscribe function.
 */
function createBalanceListener(
  docId: string,
  mapData: (data: Record<string, any>) => Partial<CreditStoreState>,
  onEmpty: () => void,
  set: (partial: Partial<CreditStoreState>) => void,
  logTag: string,
  resync: () => void,
): () => void {
  const db = firebaseHelper.db;
  if (!db) return () => {};

  return onSnapshot(
    doc(db, "creditBalances", docId),
    (docSnap) => {
      if (Date.now() < optimisticUntil) return;
      if (docSnap.exists()) {
        // `lastUpdated: 0` is the invalidation sentinel the Stripe webhook
        // writes after a credit-pack grant (issue #3891). It touches only
        // lastUpdated, so stripeCredits here is still pre-grant — committing
        // it would re-display the stale balance. Re-sync instead; that writes
        // a real balance and timestamp, so the next snapshot cannot loop.
        if (toMillis(docSnap.data().lastUpdated) === 0) {
          if (!sentinelHandledFor.has(docId)) {
            sentinelHandledFor.add(docId);
            resync();
          }
          return;
        }
        sentinelHandledFor.delete(docId);
        set({
          ...mapData(docSnap.data()),
          pendingArtifactCredits: 0,
          hideTeamBalance: false,
          isLoading: false,
          error: null,
        });
      } else {
        onEmpty();
      }
    },
    (error) => {
      console.error(`[CreditStore] ${logTag} listener error:`, error);
      set({ isLoading: false, error: error.message });
    },
  );
}

export const useCreditStore = create<CreditStoreState>((set, get) => ({
  balance: null,
  teamBalance: null,
  designFirmId: null,
  hideTeamBalance: false,
  pendingArtifactCredits: 0,
  isLoading: false,
  error: null,

  addPendingArtifactCredits: (n) =>
    set((s) => ({ pendingArtifactCredits: s.pendingArtifactCredits + n })),

  fetchBalance: async (force = false, uid?: string) => {
    // Dedup: coalesce concurrent force=true calls onto a single in-flight promise.
    // force=false hits the cached endpoint — no Stripe risk, no dedup needed.
    if (force && walletFetchInFlight) return walletFetchInFlight;

    const body = async () => {
      set({ isLoading: true, ...(force ? { error: null } : {}) });
      try {
        const data = await callCreditsApi<{
          ok: boolean;
          balance: {
            stripeCredits: number;
            freePersonalCredits: number;
            combinedBalance: number;
            lastUpdated?: number | null;
          };
        }>(`/api/credits/wallet${force ? "?force=true" : ""}`);

        if (!data?.ok) throw new Error("Failed to load wallet");

        set({
          balance: {
            totalBalance: data.balance.combinedBalance,
            stripeBalance: data.balance.stripeCredits,
            freeBalance: data.balance.freePersonalCredits,
            cachedUnbilledUsage: 0,
            lastUpdated: data.balance.lastUpdated ?? null,
          },
          hideTeamBalance: false,
          isLoading: false,
          error: null,
        });

        // Trigger signup bonus (deduped, fire-and-forget).
        // If granted, the Firestore listener in subscribeToBalance picks up the updated balance.
        const resolvedUid = uid ?? currentUid;
        if (resolvedUid) {
          checkSignupBonus(resolvedUid);
        }
      } catch (error: unknown) {
        console.error("[CreditStore] fetchBalance error:", error);
        set({
          isLoading: false,
          error: getErrorMessage(error, "Failed to load balance"),
        });
      }
    };

    if (force) {
      walletFetchInFlight = body().finally(() => {
        walletFetchInFlight = null;
      });
      return walletFetchInFlight;
    }

    return body();
  },

  fetchTeamBalance: async (designFirmId: string, force = false) => {
    // Dedup: coalesce concurrent force=true calls per firmId.
    if (force) {
      const existing = teamWalletFetchInFlight.get(designFirmId);
      if (existing) return existing;
    }

    const body = async () => {
      set({ isLoading: true, designFirmId, ...(force ? { error: null } : {}) });
      try {
        const data = await callCreditsApi<{
          ok: boolean;
          balance: {
            stripeCredits: number;
            lastUpdated?: number | null;
          };
        }>(
          `/api/credits/team/${designFirmId}/wallet${force ? "?force=true" : ""}`,
        );

        if (!data?.ok) throw new Error("Failed to load team wallet");

        set({
          teamBalance: {
            totalBalance: data.balance.stripeCredits,
            stripeBalance: data.balance.stripeCredits,
            freeBalance: 0,
            cachedUnbilledUsage: 0,
            lastUpdated: data.balance.lastUpdated ?? null,
          },
          hideTeamBalance: false,
          isLoading: false,
          error: null,
        });
      } catch (error: unknown) {
        console.error("[CreditStore] fetchTeamBalance error:", error);
        const rawMessage =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : "";
        let parsedMessage = rawMessage;
        if (rawMessage.startsWith("{")) {
          try {
            const parsed = JSON.parse(rawMessage);
            parsedMessage =
              typeof parsed?.error === "string" ? parsed.error : rawMessage;
          } catch {
            parsedMessage = rawMessage;
          }
        }
        if (parsedMessage === "TEAM_STRIPE_CUSTOMER_NOT_CONFIGURED") {
          set({
            teamBalance: null,
            hideTeamBalance: true,
            isLoading: false,
            error: null,
          });
          return;
        }
        set({
          isLoading: false,
          error: parsedMessage || "Failed to load team balance",
        });
      }
    };

    if (force) {
      const p = body().finally(() => {
        teamWalletFetchInFlight.delete(designFirmId);
      });
      teamWalletFetchInFlight.set(designFirmId, p);
      return p;
    }

    return body();
  },

  checkSufficientCredits: (required: number) => {
    const { teamBalance, balance, designFirmId, hideTeamBalance } = get();

    // Team users: check team balance only
    if (designFirmId) {
      if (hideTeamBalance) return false;
      if (!teamBalance) return false;
      return teamBalance.totalBalance >= required;
    }

    // Personal users: check personal balance
    if (!balance) return false;
    return balance.totalBalance >= required;
  },

  subscribeToBalance: (uid: string) => {
    if (personalUnsubscribe) {
      personalUnsubscribe();
      personalUnsubscribe = null;
    }

    if (!uid) return;

    currentUid = uid;
    // Switching to the personal subscription means Personal is the active
    // wallet — clear the team residue (designFirmId / teamBalance /
    // hideTeamBalance) left behind by a previous subscribeToTeamBalance.
    // Before this, only resetStore (logout) cleared designFirmId, so one
    // visit to a team workspace permanently re-pointed
    // checkSufficientCredits at the stale team balance: it blocked users
    // who had personal credits, or let through users who didn't. (The
    // backend charge is stamped per concept and was never affected — this
    // is the client pre-flight gate only.)
    set({
      isLoading: true,
      error: null,
      designFirmId: null,
      teamBalance: null,
      hideTeamBalance: false,
    });

    personalUnsubscribe = createBalanceListener(
      uid,
      (data) => ({
        balance: {
          totalBalance:
            (data.stripeCredits ?? 0) + (data.freePersonalCredits ?? 0),
          stripeBalance: data.stripeCredits ?? 0,
          freeBalance: data.freePersonalCredits ?? 0,
          cachedUnbilledUsage: data.cachedUnbilledUsage ?? 0,
          lastUpdated: toMillis(data.lastUpdated),
        },
      }),
      () => set({ balance: null, isLoading: false }),
      set,
      "Balance",
      () => {
        void get().fetchBalance(true, uid);
      },
    );

    // Trigger signup bonus (deduped, fire-and-forget)
    checkSignupBonus(uid);
  },

  subscribeToTeamBalance: (designFirmId: string) => {
    if (teamUnsubscribe) {
      teamUnsubscribe();
      teamUnsubscribe = null;
    }

    if (!designFirmId) return;

    // When switching to a DIFFERENT firm (multi-team), drop the previous
    // team's balance and hide-flag so the window before the new listener's
    // first snapshot reads as "loading" — not as the other team's wallet.
    // Re-subscribing to the same firm keeps the current value (no flicker).
    const switchingFirms = get().designFirmId !== designFirmId;
    set({
      isLoading: true,
      designFirmId,
      error: null,
      ...(switchingFirms ? { teamBalance: null, hideTeamBalance: false } : {}),
    });

    teamUnsubscribe = createBalanceListener(
      `team_${designFirmId}`,
      (data) => ({
        teamBalance: {
          totalBalance: data.stripeCredits ?? 0,
          stripeBalance: data.stripeCredits ?? 0,
          freeBalance: 0,
          cachedUnbilledUsage: data.cachedUnbilledUsage ?? 0,
          lastUpdated: toMillis(data.lastUpdated),
        },
      }),
      () => set({ teamBalance: null, hideTeamBalance: true, isLoading: false }),
      set,
      "Team balance",
      () => {
        void get().fetchTeamBalance(designFirmId, true);
      },
    );
  },

  unsubscribeAll: () => {
    if (personalUnsubscribe) {
      personalUnsubscribe();
      personalUnsubscribe = null;
    }
    if (teamUnsubscribe) {
      teamUnsubscribe();
      teamUnsubscribe = null;
    }
  },

  resetStore: () => {
    get().unsubscribeAll();
    currentUid = null;
    bonusCheckedForUid = null;
    bonusInFlightUid = null;
    bonusInFlight = null;
    walletFetchInFlight = null;
    teamWalletFetchInFlight.clear();
    optimisticUntil = 0;
    sentinelHandledFor.clear();
    set({
      balance: null,
      teamBalance: null,
      designFirmId: null,
      hideTeamBalance: false,
      pendingArtifactCredits: 0,
      isLoading: false,
      error: null,
    });
  },
}));
